import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgeCheck, Clock4, Award, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import Seo from "@/components/Seo";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { COMPANY, SERVICES, STATS } from "@/lib/constants";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Trusted Asbestos Experts NSW"
        description="Asbestos inspections, sampling, air monitoring, risk assessments and clearance certificates by ISGS Asbestos Consulting. NATA-accredited, 18+ years experience, 24/7 emergency response across NSW."
        path="/"
      />

      {/* HERO */}
      <section className="relative bg-[#0E2A5C] text-white overflow-hidden diag-bottom" data-testid="hero-section">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-[#A6CD3C]/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 w-[420px] h-[420px] rounded-full bg-[#A6CD3C]/8 blur-3xl" />

        <div className="container-x relative z-10 pt-16 pb-32 lg:pt-24 lg:pb-44">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 rise">
              <div className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#FF5A4E]/25 to-[#FFB627]/25 border border-[#FFB627]/40 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB627] animate-pulse" />
                <span className="text-[12px] tracking-[0.18em] uppercase font-medium text-white">NATA-Accredited · 24/7 Emergency Response</span>
              </div>
              <h1 className="text-[44px] md:text-[60px] lg:text-[72px] font-bold leading-[0.95] tracking-tight">
                <span className="text-[#A6CD3C]">Asbestos</span> managed{"\u00A0"}with{" "}
                <span className="text-[#A6CD3C]">Precision</span> <span className="text-white">and</span>
                <span className="block mt-2"><span className="text-[#A6CD3C]">Safety</span> treated as <span className="italic font-light text-white/90">non-negotiable.</span></span>
              </h1>
              <p className="mt-7 text-[17px] leading-relaxed text-white/80 max-w-2xl">
                ISGS Consulting Group are the family founded leading Asbestos & Hazardous Materials Consultants in NSW, Greater Australia and the World Market. Specialising in end-to-end Asbestos and HAZMAT advice and surveys, to prevent people, the built and natural environment from contamination and exposure.
              </p>

              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
                  Request a Consultation <ArrowRight size={18} />
                </Link>
                <a href={`tel:${COMPANY.phoneAltTel}`} className="btn-ghost" data-testid="hero-cta-call">
                  <Phone size={18} /> {COMPANY.phoneAlt}
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-[12.5px] text-white/70">
                <div className="inline-flex items-center gap-2 whitespace-nowrap"><ShieldCheck size={15} className="text-[#A6CD3C]" /> Licensed & Insured</div>
                <div className="inline-flex items-center gap-2 whitespace-nowrap"><BadgeCheck size={15} className="text-[#A6CD3C]" /> NATA-Accredited Labs</div>
                <div className="inline-flex items-center gap-2 whitespace-nowrap"><Clock4 size={15} className="text-[#A6CD3C]" /> 24/7 Emergency Response</div>
                <div className="inline-flex items-center gap-2 whitespace-nowrap"><Award size={15} className="text-[#A6CD3C]" /> 18+ Years Experience</div>
              </div>
            </div>

            <div className="lg:col-span-5 rise rise-d2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#A6CD3C]/30 via-[#A6CD3C]/10 to-transparent rounded-3xl blur-2xl" />
                <a
                  href="#about-video"
                  onClick={(e) => { e.preventDefault(); }}
                  className="relative block bg-white/[0.06] backdrop-blur-md border border-white/15 rounded-3xl p-5 group cursor-pointer hover:border-[#A6CD3C]/60 transition-colors"
                  data-testid="about-video-cta"
                >
                  <div className="relative aspect-video rounded-2xl overflow-hidden bg-[#0A1F47]">
                    <img src={COMPANY.logo} alt="About ISGS Asbestos Consulting" className="w-full h-full object-contain p-8" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F47]/85 via-[#0A1F47]/30 to-transparent" />
                    {/* Play button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <div className="absolute inset-0 rounded-full bg-[#A6CD3C] animate-ping opacity-30" />
                        <div className="relative w-20 h-20 rounded-full bg-[#A6CD3C] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="#0E2A5C">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    {/* Bottom label */}
                    <div className="absolute bottom-0 inset-x-0 p-5">
                      <div className="text-[10.5px] uppercase tracking-[0.25em] text-[#A6CD3C] font-bold mb-1">About ISGS</div>
                      <div className="text-white text-[15px] font-semibold">Watch our story · Coming soon</div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS TICKER */}

      {/* WHY · HOW · WHAT */}
      <section className="section-pad bg-vibrant-1 relative" data-testid="why-how-what">
        <div className="container-x">
          <div className="grid md:grid-cols-3 gap-6">
            {/* WHY WE DO IT */}
            <div className="bg-white rounded-2xl p-8 border border-[#0E2A5C]/10 hover:border-[#A6CD3C] transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(14,42,92,0.3)]" data-testid="why-we-do-it">
              <div className="eyebrow mb-5">Why we do it</div>
              <h3 className="text-[22px] font-bold text-[#0E2A5C] mb-4 leading-snug">Lives depend on us getting it right.</h3>
              <p className="text-[#5B6478] text-[14.5px] leading-relaxed">
                Asbestos kills over 4,000 Australians every year — more than the national road toll. We exist to protect people, the built environment and the natural world from exposure and contamination.
              </p>
            </div>

            {/* HOW WE DO IT */}
            <div className="bg-white rounded-2xl p-8 border border-[#0E2A5C]/10 hover:border-[#A6CD3C] transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(14,42,92,0.3)]" data-testid="how-we-do-it">
              <div className="eyebrow mb-5">How we do it</div>
              <h3 className="text-[22px] font-bold text-[#0E2A5C] mb-4 leading-snug">Rigorous. Independent. Documented.</h3>
              <p className="text-[#5B6478] text-[14.5px] leading-relaxed">
                All samples are analysed exclusively through NATA-accredited laboratories. We work as independent consultants — never as removalists — to deliver objective reporting that holds up to audit.
              </p>
            </div>

            {/* WHAT WE DO */}
            <div className="bg-white rounded-2xl p-8 border border-[#0E2A5C]/10 hover:border-[#A6CD3C] transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(14,42,92,0.3)]" data-testid="what-we-do">
              <div className="eyebrow mb-5">What we do</div>
              <h3 className="text-[22px] font-bold text-[#0E2A5C] mb-4 leading-snug">End-to-end Asbestos & HAZMAT services.</h3>
              <p className="text-[#5B6478] text-[14.5px] leading-relaxed">
                Inspections, sampling, risk assessments, air monitoring, clearance certificates and full remediation project management — across asbestos, lead, mould and other hazardous materials.
              </p>
            </div>
          </div>

          {/* Full-width View all services pill */}
          <Link
            to="/services"
            className="mt-10 w-full bg-[#0E2A5C] hover:bg-[#A6CD3C] text-white hover:text-[#0E2A5C] py-5 px-8 rounded-full font-semibold flex items-center justify-center gap-3 transition-all group border-2 border-[#0E2A5C] hover:border-[#A6CD3C] shadow-[0_10px_30px_-12px_rgba(14,42,92,0.4)]"
            data-testid="view-all-services"
          >
            <span className="text-[15px] tracking-wide">View all services</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* AWARENESS BANNER — drives traffic to Health Effects page */}
      <section className="relative text-white section-pad overflow-hidden" data-testid="awareness-banner">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://customer-assets-eiarnc6j.emergentagent.net/job_draft-preview-11/artifacts/0fr6g8ay_20161118_142622.webp')" }}
        />
        {/* Dark overlay — lightened so weathered asbestos roof reads through while text stays legible */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0E2A5C]/60 via-[#0A1F47]/50 to-[#0E2A5C]/45" />
        <div className="absolute inset-0 grid-overlay opacity-15" />
        <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-[#A6CD3C]/15 to-transparent" />

        <div className="container-x relative z-10 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#FF5A4E]/20 border border-[#FF5A4E]/40 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A4E] animate-pulse" />
            <span className="text-[11.5px] tracking-[0.22em] uppercase font-semibold text-white">Public Health Crisis</span>
          </div>
          <h2 className="text-[34px] md:text-[54px] leading-[1.05] mb-6 font-bold drop-shadow-lg">
            One of Australia's most <span className="text-[#A6CD3C]">significant</span><br className="hidden md:block" /> ongoing health crises.
          </h2>
          <p className="text-white/90 text-[16.5px] leading-relaxed max-w-2xl mx-auto mb-9 drop-shadow">
            Asbestos exposure has no known safe level. Symptoms can take 10–40+ years to appear — making early identification critical.
          </p>
          <Link to="/health-effects" className="btn-primary" data-testid="learn-health-effects-btn">
            Read the Health Impact <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS — placed in Careers section block */}
      <TestimonialsCarousel />

      {/* CONTACT CTA — corporate navy */}
      <section className="section-pad bg-white" data-testid="home-contact-cta">
        <div className="container-x">
          <div className="rounded-[24px] p-10 md:p-16 relative overflow-hidden shadow-[0_30px_60px_-30px_rgba(14,42,92,0.5)]">
            {/* Background image — cover so it fills the box edge-to-edge */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url('https://customer-assets-eiarnc6j.emergentagent.net/job_draft-preview-11/artifacts/yctehjxb_WhatsApp%20Image%202026-05-18%20at%2010.18.36%20PM.jpeg')" }}
            />
            {/* Navy overlay — lightened so the field image reads through while text stays legible */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0E2A5C]/55 via-[#0A1F47]/45 to-[#0E2A5C]/45" />
            <div className="absolute inset-0 grid-overlay opacity-15" />
            <div className="absolute right-0 top-0 h-full w-[40%] bg-gradient-to-l from-[#A6CD3C]/12 to-transparent" />
            <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
              <div className="inline-flex items-center gap-2 bg-[#A6CD3C]/15 border border-[#A6CD3C]/40 rounded-full px-3.5 py-1 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C] animate-pulse" />
                <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#A6CD3C]">Rapid response</span>
              </div>
              <h2 className="text-[30px] md:text-[42px] text-white leading-[1.05] font-bold">
                Unexpected Find of Asbestos?<br />
                <span className="font-light italic text-white/85">Or think you have it?</span>
              </h2>
              <p className="mt-5 text-white/80 max-w-xl text-[15.5px]">
                Talk to a certified ISGS Representative now. For Subject Matter Experts and immediate Asbestos Specific advice with International Compliance.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:justify-center">
                <a href={`tel:${COMPANY.phoneAltTel}`} className="btn-primary justify-center" data-testid="cta-phone-btn">
                  <Phone size={18} /> {COMPANY.phoneAlt}
                </a>
                <Link to="/contact" className="bg-transparent text-white py-3.5 px-6 rounded-full font-semibold text-center hover:bg-white hover:text-[#0E2A5C] transition-colors border-2 border-white/40 hover:border-white" data-testid="cta-contact-btn">
                  Send a Message
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
